import { Injectable, inject } from '@angular/core';
import { combineLatest, merge, Observable, switchMap } from 'rxjs';
import { map, scan } from 'rxjs/operators';
import { FilterSelectItem } from '../../components/checkbox-facette/checkbox-liste/filterSelectItem';
import { AnbieterService } from '../facetten/instance/anbieter.service';
import { AnbieterUrlService } from '../facetten/instance/anbieterurl.service';
import { BarrierefreiheitService } from '../facetten/instance/barrierefreiheit.service';
import { BarrierefreiheitUrlService } from '../facetten/instance/barrierefreiheiturl.service';
import { BeginnterminService } from '../facetten/instance/beginntermin.service';
import { BeginnterminUrlService } from '../facetten/instance/beginnterminurl.service';
import { DauerService } from '../facetten/instance/dauer.service';
import { DauerUrlService } from '../facetten/instance/dauerurl.service';
import { FoerderartService } from '../facetten/instance/foerderart.service';
import { FoerderartUrlService } from '../facetten/instance/foerderarturl.service';
import { KostenService } from '../facetten/instance/kosten.service';
import { KostenUrlService } from '../facetten/instance/kostenurl.service';
import { LernformService } from '../facetten/instance/lernformen-facetten/lernform.service';
import { LernformUrlService } from '../facetten/instance/lernformen-facetten/lernformurl.service';
import { OrtFacettenService } from '../facetten/instance/ort-facetten.service';
import { RegionenService } from '../facetten/instance/regionen.service';
import { RegionenUrlService } from '../facetten/instance/regionenurl.service';
import { UnterrichtszeitService } from '../facetten/instance/unterrichtszeit.service';
import { UnterrichtszeitUrlService } from '../facetten/instance/unterrichtszeiturl.service';
import { WeiterbildungsartService } from '../facetten/instance/weiterbildungsart.service';
import { WeiterbildungsartUrlService } from '../facetten/instance/weiterbildungsarturl.service';
import { ErweiterteSucheService } from './erweiterte-suche.service';

/**
 * Repräsentation der Facetten-Optionen, die selektiert und aktiv sind.
 */
export interface AktiverUrlFilter {
  urlParam: string;
  key: string;
  bezeichnung: string;
}

/**
 * Service-interne Zwischen-Darstellung der AktiverUrlFilter
 */
interface UrlFilter {
  urlParam: string;
  filterKeys: string[];

  filterBez: string[];
}

/**
 * Dieser Service listet alle aktiven Optionen der Facetten auf. Aktive Optionen sind selektiert
 * und haben mindestens einen Termin zugeordnet.
 */
@Injectable({ providedIn: 'root' })
export class AktiveFilterService {
  private readonly anbieterService = inject(AnbieterService);
  private readonly regionenService = inject(RegionenService);
  private readonly dauerService = inject(DauerService);
  private readonly unterrichtszeitService = inject(UnterrichtszeitService);
  private readonly beginnterminService = inject(BeginnterminService);
  private readonly lernformService = inject(LernformService);
  private readonly weiterbildungsartUrlService = inject(WeiterbildungsartService);
  private readonly erweiterteSucheService = inject(ErweiterteSucheService);
  private readonly kostenService = inject(KostenService);
  private readonly barrierefreiheitService = inject(BarrierefreiheitService);
  private readonly foerderartBundesweitService = inject(FoerderartService);
  private readonly ortFacettenService = inject(OrtFacettenService);

  private readonly aktiveRegionen$: Observable<UrlFilter> = this.regionenService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, RegionenUrlService.URL_PARAMETER))
    );

  private readonly aktiveAnbieter$: Observable<UrlFilter> = this.anbieterService.anbieter$.pipe(
    map((anbieter) => anbieter.filter((a) => a.ausgewaehlt)),
    map((anbieter) => ({
      filterKeys: anbieter.map(({ id }) => id),
      urlParam: AnbieterUrlService.URL_PARAMETER,
      filterBez: anbieter.map(({ name }) => name)
    }))
  );

  private readonly aktiveKosten$: Observable<UrlFilter> = this.kostenService.getSelectItems$().pipe(
    map(filterActiveAndSelected),
    map((kosten) => mapSelectItemsToUrlFilter(kosten, KostenUrlService.URL_PARAMETER))
  );

  private readonly aktiveLernOderUnterrichtsformen$: Observable<UrlFilter> =
    this.erweiterteSucheService.erweiterteSucheActive$.pipe(
      switchMap((active) => {
        if (active) {
          return this.aktiveLernformen$;
        } else {
          return this.aktiveUnterrichtsFormen$;
        }
      })
    );

  private readonly aktiveUnterrichtsFormen$: Observable<UrlFilter> = this.lernformService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, LernformUrlService.URL_PARAMETER))
    );

  private readonly aktiveLernformen$: Observable<UrlFilter> = this.lernformService
    .getAllAktiveLernformen$()
    .pipe(
      map((lernformen) => lernformen.filter((form) => form.selected)),
      map((items) => mapSelectItemsToUrlFilter(items, LernformUrlService.URL_PARAMETER))
    );

  private readonly aktiveUnterrichtszeiten$: Observable<UrlFilter> = this.unterrichtszeitService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, UnterrichtszeitUrlService.URL_PARAMETER))
    );

  private readonly aktiveBeginntermine$: Observable<UrlFilter> = this.beginnterminService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, BeginnterminUrlService.URL_PARAMETER))
    );

  private readonly aktiveDauerGruppe$: Observable<UrlFilter> = this.dauerService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) =>
        mapSelectItemsToUrlFilter(
          items,
          DauerUrlService.URL_PARAMETER,
          () =>
            // Placeholder-Wert, der dazu führt, das immer alle
            // Dauer-URL-Parameter beim Löschen der Option entfernt werden
            'all'
        )
      )
    );

  private readonly aktiveWeiterbildungsarten$: Observable<UrlFilter> =
    this.weiterbildungsartUrlService.getSelectItems$().pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, WeiterbildungsartUrlService.URL_PARAMETER))
    );

  private readonly aktiveFoerderarten$: Observable<UrlFilter> = this.foerderartBundesweitService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, FoerderartUrlService.URL_PARAMETER))
    );

  private readonly aktiveBarrierefreiheit$: Observable<UrlFilter> = this.barrierefreiheitService
    .getSelectItems$()
    .pipe(
      map(filterActiveAndSelected),
      map((items) => mapSelectItemsToUrlFilter(items, BarrierefreiheitUrlService.URL_PARAMETER))
    );

  private readonly mapToAktiverUrlFilterAndFlatten = (
    acc: AktiverUrlFilter[],
    value: UrlFilter
  ): AktiverUrlFilter[] => {
    return [
      ...acc.filter((alt) => alt.urlParam !== value.urlParam),
      ...value.filterKeys.map(
        (element, i) =>
          ({
            key: element,
            urlParam: value.urlParam,
            bezeichnung: value.filterBez[i]
          }) as AktiverUrlFilter
      )
    ];
  };

  /**
   * Alle aktiven Optionen der Facetten in ihrer fixen Darstellungsreihenfolge.
   * Aktive Optionen sind selektiert und haben mindestens einen Termin zugeordnet.
   */
  public aktiverUrlFilter$: Observable<AktiverUrlFilter[]> = merge(
    this.aktiveRegionen$,
    this.aktiveUnterrichtszeiten$,
    this.aktiveAnbieter$,
    this.aktiveLernOderUnterrichtsformen$,
    this.aktiveBeginntermine$,
    this.aktiveDauerGruppe$,
    this.aktiveWeiterbildungsarten$,
    this.aktiveKosten$,
    this.aktiveBarrierefreiheit$,
    this.aktiveFoerderarten$
  ).pipe(scan(this.mapToAktiverUrlFilterAndFlatten, []));

  public anzahlAktiveFilter$: Observable<number> = combineLatest([
    this.aktiverUrlFilter$,
    this.ortFacettenService.isOrtSelected$
  ]).pipe(
    map(([filter, isOrtSelected]) => {
      // Wenn Ort ausgewählt ist, dann addiere 2, weil dann auch immer ein Umkreis ausgewählt ist
      return filter?.length + (isOrtSelected ? 2 : 0);
    })
  );
}

const filterActiveAndSelected = (items: FilterSelectItem[]) =>
  items.filter((item) => item.active && item.selected);

const mapSelectItemsToUrlFilter = (
  items: FilterSelectItem[],
  urlParam: string,
  mappingMethod: (item: FilterSelectItem) => string = defaultMappingMethod
): UrlFilter => {
  return {
    filterKeys: items.map(mappingMethod),
    urlParam: urlParam,
    filterBez: items.map((item) => item.label)
  };
};

const defaultMappingMethod = (item: FilterSelectItem): string => item.key;
